import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { toFullMediaThumb, toFullMediaUrl, toVideoPosterUrl } from '../../utils/mediaUrl';
import UnifiedVideo from './UnifiedVideo';
import './Lightbox.css';

/**
 * Lightbox — full-screen premium media viewer.
 *
 * - Photo & video support
 * - Swipe / arrow navigation between media items
 * - Keyboard: ArrowLeft, ArrowRight, Esc
 * - Custom video controls: play/pause, mute, scrubber
 * - Smooth slide transition
 * - Thumbnail strip
 */
const Lightbox = ({ open, media = [], startIndex = 0, onClose }) => {
  const [index, setIndex] = useState(startIndex);
  const [progress, setProgress] = useState(0);
  const videoApiRef = useRef(null);
  const startX = useRef(0);
  const swiping = useRef(false);
  const trackRef = useRef(null);

  useEffect(() => {
    if (open) {
      setIndex(startIndex);
    }
  }, [open, startIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % Math.max(media.length, 1));
      if (e.key === 'ArrowLeft')  setIndex((i) => (i - 1 + Math.max(media.length, 1)) % Math.max(media.length, 1));
      if (e.key === ' ' && media[index]?.type === 'video') {
        e.preventDefault();
        videoApiRef.current?.togglePlay?.();
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, media.length, index]);

  useEffect(() => {
    setProgress(0);
  }, [index]);

  const onPointerDown = (e) => {
    const point = e.touches ? e.touches[0] : e;
    startX.current = point.clientX;
    swiping.current = true;
  };
  const onPointerUp = (e) => {
    if (!swiping.current) return;
    swiping.current = false;
    const point = e.changedTouches ? e.changedTouches[0] : e;
    const dx = point.clientX - startX.current;
    if (Math.abs(dx) > 60) {
      if (dx < 0) setIndex((i) => (i + 1) % Math.max(media.length, 1));
      else setIndex((i) => (i - 1 + Math.max(media.length, 1)) % Math.max(media.length, 1));
    }
  };

  if (typeof document === 'undefined') return null;
  if (!open) return null;

  const current = media[index] || null;
  const total = media.length;
  const isAdjacent = (candidateIndex) => total > 1 && (
    candidateIndex === (index + 1) % total
    || candidateIndex === (index - 1 + total) % total
  );

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="gm-lightbox"
        role="dialog"
        aria-modal="true"
        aria-label="Visualizador de media"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.22 }}
      >
        <div className="gm-lightbox__topbar">
          <div className="gm-lightbox__counter">
            {total > 1 ? `${index + 1} / ${total}` : ''}
          </div>
          <button
            type="button"
            className="gm-lightbox__close"
            onClick={onClose}
            aria-label="Fechar"
          >
            <X size={20} strokeWidth={1.75} />
          </button>
        </div>

        {total > 1 && (
          <>
            <button
              type="button"
              className="gm-lightbox__nav gm-lightbox__nav--prev"
              onClick={() => setIndex((i) => (i - 1 + total) % total)}
              aria-label="Anterior"
            >
              <ChevronLeft size={24} strokeWidth={1.75} />
            </button>
            <button
              type="button"
              className="gm-lightbox__nav gm-lightbox__nav--next"
              onClick={() => setIndex((i) => (i + 1) % total)}
              aria-label="Seguinte"
            >
              <ChevronRight size={24} strokeWidth={1.75} />
            </button>
          </>
        )}

        <div
          className="gm-lightbox__viewport"
          onTouchStart={onPointerDown}
          onTouchEnd={onPointerUp}
          onMouseDown={onPointerDown}
          onMouseUp={onPointerUp}
          onMouseLeave={onPointerUp}
        >
          <div
            ref={trackRef}
            className="gm-lightbox__track"
            style={{ transform: `translate3d(${-index * 100}%, 0, 0)` }}
          >
            {media.map((m, i) => (
              <div className="gm-lightbox__slide" key={`${i}-${m.src}`}>
                {m.type === 'video' ? (
                  <UnifiedVideo
                    ref={i === index ? videoApiRef : undefined}
                    containerClassName="gm-lightbox__video-container"
                    className="gm-lightbox__media"
                    src={m.src}
                    poster={m.poster || undefined}
                    active={i === index}
                    visible={i === index}
                    load={i === index || isAdjacent(i)}
                    observeVisibility={false}
                    resetOnActivate
                    showSoundButton={i === index}
                    soundButtonClassName="gm-video-sound-button--lightbox"
                    preload={i === index ? 'auto' : (isAdjacent(i) ? 'metadata' : 'none')}
                    fetchPriority={i === index ? 'high' : 'low'}
                    ariaLabel="Reproduzir vídeo com som"
                    onTimeUpdate={(e) => {
                      if (i === index) {
                        const d = e.currentTarget.duration || 0;
                        setProgress(d ? e.currentTarget.currentTime / d : 0);
                      }
                    }}
                  >
                    {i === index && (
                      <div className="gm-lightbox__video-bar">
                        <div
                          className="gm-lightbox__video-bar-fill"
                          style={{ transform: `scaleX(${progress})` }}
                        />
                      </div>
                    )}
                  </UnifiedVideo>
                ) : (
                  <img
                    className="gm-lightbox__media"
                    // The active image is the original high-quality upload.
                    // The lightbox is where users inspect detail, so a
                    // 1024px thumb must never be scaled across the viewer.
                    src={i === index
                      ? toFullMediaUrl(m.src)
                      : (isAdjacent(i) ? toFullMediaThumb(m.src, { width: 640 }) : undefined)}
                    alt=""
                    loading={i === index ? 'eager' : 'lazy'}
                    fetchPriority={i === index ? 'high' : 'low'}
                    decoding="async"
                    draggable={false}
                    onError={(event) => {
                      if (i === index && !event.currentTarget.dataset.originalFallback) {
                        event.currentTarget.dataset.originalFallback = 'true';
                        event.currentTarget.removeAttribute('srcset');
                        event.currentTarget.src = m.src;
                      }
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {(current?.caption || current?.text || current?.title) && (
          <div className="gm-lightbox__caption">
            {current.caption || current.text || current.title}
          </div>
        )}

        {total > 1 && (
          <div className="gm-lightbox__thumbs" role="tablist" aria-label="Miniaturas">
            {media.map((m, i) => (
              <button
                key={i}
                type="button"
                className={`gm-lightbox__thumb ${i === index ? 'gm-lightbox__thumb--active' : ''}`}
                onClick={() => setIndex(i)}
                aria-label={`Ir para ${i + 1}`}
                aria-selected={i === index}
                role="tab"
              >
                {m.type === 'video' ? (
                  <span className="gm-lightbox__thumb-video" aria-hidden="true">
                    {(m.poster || toVideoPosterUrl(m.src)) ? (
                      <img
                        src={toFullMediaUrl(m.poster || toVideoPosterUrl(m.src))}
                        alt=""
                        draggable={false}
                        onError={(event) => { event.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <span className="gm-lightbox__thumb-video-fallback">
                        <Play size={20} strokeWidth={1.75} fill="currentColor" />
                      </span>
                    )}
                    <span className="gm-lightbox__thumb-video-badge">
                      <Play size={11} strokeWidth={2} fill="currentColor" />
                    </span>
                  </span>
                ) : (
                  <img
                    src={toFullMediaThumb(m.src, { width: 320 })}
                    alt=""
                    loading={i === index ? 'eager' : 'lazy'}
                    decoding="async"
                    draggable={false}
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
};

Lightbox.propTypes = {
  open: PropTypes.bool,
  media: PropTypes.array,
  startIndex: PropTypes.number,
  onClose: PropTypes.func,
};

export default Lightbox;
